import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, LogOut, Mail, Phone, CalendarDays, Info, Wrench, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatDate } from "../lib/format";
import { toast } from "sonner";

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2.5">
    <span className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
      <Icon className="h-4 w-4 text-muted-foreground" />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium truncate">{value || "-"}</p>
    </div>
  </div>
);

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    toast.success("Kamu telah keluar. Sampai jumpa!");
    navigate("/masuk");
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <h1 className="font-heading text-2xl font-semibold">Profil</h1>

      <Card className="rounded-2xl">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 text-primary font-heading font-bold text-2xl flex items-center justify-center">
              {(user?.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-heading text-lg font-semibold" data-testid="profile-name">{user?.name}</p>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-100 mt-1">
                <ShieldCheck className="h-3 w-3 mr-1" /> Akun Terverifikasi
              </Badge>
            </div>
          </div>
          <Separator />
          <div className="divide-y divide-border/60">
            <InfoRow icon={Mail} label="Email" value={user?.email} />
            <InfoRow icon={Phone} label="Nomor HP" value={user?.phone} />
            <InfoRow icon={CalendarDays} label="Terdaftar sejak" value={formatDate(user?.created_at)} />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold mb-3">Keamanan</h3>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-blue-50 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </span>
              <div>
                <p className="text-sm font-medium">PIN Transaksi</p>
                <p className="text-xs text-muted-foreground">Diminta di setiap pergerakan dana</p>
              </div>
            </div>
            <Badge className="bg-emerald-600" data-testid="profile-pin-status">Aktif</Badge>
          </div>
        </CardContent>
      </Card>

      <Link to="/dev-tools" data-testid="profile-devtools-link">
        <Card className="rounded-2xl hover:border-primary/30 hover:shadow-[0_10px_30px_rgba(11,18,32,0.08)] transition-[border-color,box-shadow] duration-200">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-blue-50 text-primary flex items-center justify-center shrink-0">
              <Wrench className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">Alat Developer</p>
              <p className="text-xs text-muted-foreground">Status gateway Xendit, integritas ledger, kontrol sandbox</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
          </CardContent>
        </Card>
      </Link>

      <Card className="rounded-2xl border-teal-200 bg-[#F0FBF9]">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
              <Info className="h-4 w-4 text-[#0FA5A0]" />
            </span>
            <div className="text-sm space-y-2">
              <p className="font-semibold">Tentang Aplikasi</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Quantum Pay adalah aplikasi <strong>simulasi</strong> sistem pembayaran digital (mode sandbox) untuk keperluan demo &mdash; bukan alat pembayaran yang sah dan tidak memproses uang sungguhan.
                Rel perbankan (transfer bank, Virtual Account) berjalan melalui <strong>Xendit sandbox</strong>, dengan
                fallback simulasi internal ketika izin API belum lengkap.
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Cek Nama rekening menggunakan simulasi deterministik. Status gateway dapat dipantau di halaman Alat Developer.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button variant="outline" className="w-full h-11 border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive" onClick={doLogout} data-testid="profile-logout-button">
        <LogOut className="h-4 w-4 mr-1.5" /> Keluar
      </Button>
    </div>
  );
}
